import { apiSlice } from '../apiSlice';
import { SuccessResponse, Paginated } from '../DTO/common.interface';
import {
	DeleteUserFilter,
	DeleteUserPageResDTO,
	Series21UnlockListResDTO,
	UpdateUserRequest,
	User21Filter,
	User330DetailDTO,
	UserAdvanceSeriesPageResDTO,
	UserAdvanceSeriesSinglePageResDTO,
	UserDetailDTO,
	UserFilter,
	UserPageResDTO,
	UserRequest,
	UserSeries21PageResDTO,
	UserSeriesStatisticsResDTO,
	UserTreasureFilter,
	UserTreasureRecordDetailDTO,
} from '../DTO/user/userList.interface';

export const userApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getUserList: builder.query<
			SuccessResponse<Paginated<UserPageResDTO>>,
			UserFilter
		>({
			query: (body) => ({
				url: `/web/users/page`,
				method: 'POST',
				body,
			}),
		}),
		getUserDetail: builder.query<SuccessResponse<UserDetailDTO>, string>({
			query: (userId) => ({
				url: `/web/users/${userId}`,
			}),
			providesTags: ['UserDetal'],
		}),
		createUser: builder.mutation<SuccessResponse<any>, UserRequest>({
			query: (body) => ({
				url: `/web/users`,
				method: 'POST',
				body,
			}),
		}),
		deleteUser: builder.mutation<SuccessResponse<any>, string>({
			query: (userId) => ({
				url: `/web/users/${userId}`,
				method: 'DELETE',
			}),
		}),
		freezeUser: builder.mutation<SuccessResponse<any>, string>({
			query: (userId) => ({
				url: `/web/users/${userId}/freeze`,
				method: 'PUT',
			}),
			invalidatesTags: ['UserDetal'],
		}),
		unfreezeUser: builder.mutation<SuccessResponse<any>, string>({
			query: (userId) => ({
				url: `/web/users/${userId}/unfreeze`,
				method: 'PUT',
			}),
			invalidatesTags: ['UserDetal'],
		}),
		changeUserPassword: builder.mutation<
			SuccessResponse<any>,
			{ userId: string; password: string }
		>({
			query: (body) => ({
				url: `/web/users/${body.userId}/password/change`,
				method: 'PUT',
				body,
			}),
		}),
		changeEmail: builder.mutation<
			SuccessResponse<any>,
			{ userId: string; email: string }
		>({
			query: (body) => ({
				url: `/web/users/${body.userId}/email`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['UserDetal'],
		}),
		changePhone: builder.mutation<
			SuccessResponse<any>,
			{ userId: string; email: string }
		>({
			query: (body) => ({
				url: `/web/users/${body.userId}/phone`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['UserDetal'],
		}),
		updateUserInfo: builder.mutation<SuccessResponse<any>, UpdateUserRequest>({
			query: (body) => ({
				url: `/web/users/${body.userId}/profile`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['UserDetal'],
		}),
		get330Detail: builder.query<SuccessResponse<User330DetailDTO>, string>({
			query: (userId) => ({
				url: `/web/record/${userId}/all`,
				method: 'GET',
			}),
		}),
		getDeleteUserList: builder.query<
			SuccessResponse<Paginated<DeleteUserPageResDTO>>,
			DeleteUserFilter
		>({
			query: (body) => ({
				url: `/web/users/remove-page`,
				method: 'POST',
				body,
			}),
		}),
		get21Progress: builder.query<
			SuccessResponse<UserSeriesStatisticsResDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/21/series/user/${id}/statistic`,
				method: 'GET',
			}),
		}),
		getMajorProgress: builder.query<
			SuccessResponse<UserSeriesStatisticsResDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/advance/series/user/${id}/statistic`,
				method: 'GET',
			}),
		}),
		get21List: builder.query<
			SuccessResponse<Paginated<UserSeries21PageResDTO>>,
			User21Filter
		>({
			query: (body) => ({
				url: `/web/exercise/21/series/user/${body.id}/page`,
				method: 'POST',
				body,
			}),
		}),
		get21DetailList: builder.query<
			SuccessResponse<Series21UnlockListResDTO[]>,
			{ userId: string; seriesId: string }
		>({
			query: (body) => ({
				url: `/web/exercise/21/series/unlock/list`,
				method: 'POST',
				body,
			}),
			providesTags: ['userUnlockList'],
		}),
		unlockDay: builder.mutation<
			SuccessResponse<any>,
			{ userId: string; dayId: string }
		>({
			query: (body) => ({
				url: `/web/exercise/21/day/unlock`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['userUnlockList'],
		}),
		unlockAdditional: builder.mutation<
			SuccessResponse<any>,
			{ userId: string; additionalId: string }
		>({
			query: (body) => ({
				url: `/web/exercise/21/additional/unlock`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['userUnlockList'],
		}),
		getMajorLists: builder.query<
			SuccessResponse<Paginated<UserAdvanceSeriesPageResDTO>>,
			User21Filter
		>({
			query: (body) => ({
				url: `/web/exercise/advance/series/user/${body.id}/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['userMajorList'],
		}),
		unlockMajor: builder.mutation<
			SuccessResponse<any>,
			{ id: string; seriesId: string }
		>({
			query: (body) => ({
				url: `/web/exercise/advance/series/${body.seriesId}/user/${body.id}/unlock`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['userMajorList'],
		}),
		getMajorDetailList: builder.query<
			SuccessResponse<Paginated<UserAdvanceSeriesSinglePageResDTO>>,
			User21Filter
		>({
			query: (body) => ({
				url: `/web/exercise/advance/series/${body.seriesId}/user/${body.id}/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['userMajorDetailList'],
		}),
		unlockMajorDetail: builder.mutation<
			SuccessResponse<any>,
			{ id: string; seriesId: string; singleId: string }
		>({
			query: (body) => ({
				url: `/web/exercise/advance/series/${body.seriesId}/user/${body.id}/single/${body.singleId}/unlock`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['userMajorDetailList'],
		}),

		getUserTreasure: builder.query<
			SuccessResponse<Paginated<UserTreasureRecordDetailDTO>>,
			UserTreasureFilter
		>({
			query: (body) => ({
				url: `/web/record/${body.userId}/treasure/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['userTreasureList'],
		}),
		getUserPoints: builder.query<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/record/${id}/points`,
				method: 'GET',
			}),
			providesTags: ['userPoints'],
		}),
		updateUserPoints: builder.mutation<
			SuccessResponse<any>,
			{ rewardPoints: number | string; userId: string }
		>({
			query: (body) => ({
				url: `/web/record/${body.userId}/points`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['userPoints'],
		}),
		updateUserTreasure: builder.mutation<
			SuccessResponse<any>,
			{ arr: string[]; userId: string }
		>({
			query: (body) => ({
				url: `/web/record/${body.userId}/treasure`,
				method: 'PUT',
				body: body.arr,
			}),
			invalidatesTags: ['userTreasureList'],
		}),
		deleteUserTreasure: builder.mutation<
			SuccessResponse<any>,
			{ userId: string; treasureId: string }
		>({
			query: (body) => ({
				url: `/web/record/${body.userId}/treasure/${body.treasureId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['userTreasureList'],
		}),
		updateUserTreasureTotal: builder.mutation<
			SuccessResponse<any>,
			{ userId: string; treasureId: string; userTreasureCount: number | string }
		>({
			query: (body) => ({
				url: `/web/record/${body.userId}/treasure/${body.treasureId}`,
				method: 'POST',
				body: body,
			}),
			invalidatesTags: ['userTreasureList'],
		}),
	}),
});

export const {
	useGetUserListQuery,
	useGetUserDetailQuery,
	useGet330DetailQuery,
	useGetDeleteUserListQuery,
	useCreateUserMutation,
	useDeleteUserMutation,
	useFreezeUserMutation,
	useUnfreezeUserMutation,
	useChangeUserPasswordMutation,
	useChangeEmailMutation,
	useChangePhoneMutation,
	useUpdateUserInfoMutation,
	useGet21ProgressQuery,
	useGetMajorProgressQuery,
	useGet21ListQuery,
	useGet21DetailListQuery,
	useUnlockAdditionalMutation,
	useUnlockDayMutation,
	useGetMajorListsQuery,
	useUnlockMajorMutation,
	useGetMajorDetailListQuery,
	useUnlockMajorDetailMutation,
	useGetUserTreasureQuery,
	useGetUserPointsQuery,
	useUpdateUserPointsMutation,
	useUpdateUserTreasureMutation,
	useDeleteUserTreasureMutation,
	useUpdateUserTreasureTotalMutation,
} = userApiSlice;
